import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaBriefcase,
  FaCheckCircle,
  FaClock,
  FaFilePdf,
  FaMapMarkerAlt
} from "react-icons/fa";
import { loadCareerVacancies, sendCareerApplication } from "./careerApi";
import "./Career.css";

const MAX_RESUME_SIZE_BYTES = 3 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read resume PDF."));
    reader.readAsDataURL(file);
  });
}

function CareerDetail() {
  const initialApplicationForm = {
    name: "",
    email: "",
    phone: "",
    message: ""
  };

  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [applicationForm, setApplicationForm] = useState(initialApplicationForm);
  const [resumeFile, setResumeFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [applicationStatus, setApplicationStatus] = useState("");
  const [isSubmittingApplication, setIsSubmittingApplication] = useState(false);

  useEffect(() => {
    loadCareerVacancies()
      .then((data) => {
        const vacancies = Array.isArray(data.vacancies) ? data.vacancies : [];
        const selectedJob = vacancies.find(
          (vacancy) => String(vacancy.id) === String(jobId)
        );

        if (!selectedJob) {
          setLoadError("This job vacancy is not available right now.");
          return;
        }

        setJob(selectedJob);
        setLoadError("");
      })
      .catch(() => {
        setLoadError("Unable to load this job vacancy.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [jobId]);

  const handleApplicationChange = (event) => {
    const { name, value } = event.target;

    setApplicationForm((currentData) => ({
      ...currentData,
      [name]: value
    }));
  };

  const handleResumeChange = (event) => {
    const selectedFile = event.target.files && event.target.files[0];

    setApplicationStatus("");

    if (!selectedFile) {
      setResumeFile(null);
      return;
    }

    if (selectedFile.type !== "application/pdf") {
      setResumeFile(null);
      setFileInputKey((currentKey) => currentKey + 1);
      setApplicationStatus("Please upload your resume as a PDF file.");
      return;
    }

    if (selectedFile.size > MAX_RESUME_SIZE_BYTES) {
      setResumeFile(null);
      setFileInputKey((currentKey) => currentKey + 1);
      setApplicationStatus("Resume PDF must be 3 MB or smaller.");
      return;
    }

    setResumeFile(selectedFile);
  };

  const handleApplicationSubmit = async (event) => {
    event.preventDefault();

    if (!job) {
      return;
    }

    if (!resumeFile) {
      setApplicationStatus("Please upload your resume PDF.");
      return;
    }

    setIsSubmittingApplication(true);
    setApplicationStatus("");

    try {
      const resumeDataUrl = await readFileAsDataUrl(resumeFile);

      await sendCareerApplication({
        ...applicationForm,
        jobId: job.id,
        jobTitle: job.title,
        resumeFile: {
          name: resumeFile.name,
          type: resumeFile.type,
          size: resumeFile.size,
          dataUrl: resumeDataUrl
        }
      });

      setApplicationForm(initialApplicationForm);
      setResumeFile(null);
      setFileInputKey((currentKey) => currentKey + 1);
      setApplicationStatus("Application submitted successfully.");
    } catch (error) {
      setApplicationStatus(error.message || "Unable to submit application.");
    } finally {
      setIsSubmittingApplication(false);
    }
  };

  const renderSubmitButtonText = () => {
    if (isSubmittingApplication) {
      return "Submitting...";
    }

    return "Submit Application";
  };

  return (
    <div className="career-wrapper">
      <section className="career-hero admin-career-hero">
        <div className="career-hero-content">
          <span className="career-label">JOB DETAILS</span>
          <h1>{job ? job.title : "Career Opening"}</h1>
          <p>
            Review the full role details and submit your application for the
            next step.
          </p>
        </div>
      </section>

      <section className="career-detail-section">
        {isLoading ? (
          <div className="career-empty-state">
            <FaBriefcase />
            <h3>Loading job details</h3>
            <p>Please wait while we check this vacancy.</p>
          </div>
        ) : loadError ? (
          <div className="career-empty-state">
            <FaBriefcase />
            <h3>Job not found</h3>
            <p>{loadError}</p>
            <Link className="career-apply-link" to="/career">
              Back to Careers
            </Link>
          </div>
        ) : (
          <div className="career-detail-layout">
            <article className="career-detail-card">
              <Link className="career-back-link" to="/career">
                Back to all jobs
              </Link>
              <div className="career-job-top">
                <FaBriefcase />
                <span>{job.type}</span>
              </div>
              <h2>{job.title}</h2>
              <div className="career-job-meta">
                {job.department && <span>{job.department}</span>}
                {job.location && (
                  <span>
                    <FaMapMarkerAlt /> {job.location}
                  </span>
                )}
                {job.experience && (
                  <span>
                    <FaClock /> {job.experience}
                  </span>
                )}
              </div>
              <p>{job.description}</p>
              {job.requirements && (
                <div className="career-requirements">
                  <strong>Requirements</strong>
                  <p>{job.requirements}</p>
                </div>
              )}
              <small className="career-detail-date">
                Posted {job.postedDate}
                {job.deadline ? ` | Apply before ${job.deadline}` : ""}
              </small>
              {!isApplying && (
                <button
                  type="button"
                  className="career-apply-link"
                  onClick={() => setIsApplying(true)}
                >
                  Apply Now
                </button>
              )}
            </article>

            {isApplying && (
              <form
                className="career-vacancy-form career-detail-form"
                onSubmit={handleApplicationSubmit}
              >
                <div className="career-admin-heading">
                  <span>Apply Now</span>
                  <h2>Submit Application</h2>
                  <p>
                    Fill your details below. Your application will be stored in
                    the admin panel.
                  </p>
                </div>

                <div className="career-form-grid career-application-grid">
                  <div className="career-input-group">
                    <label>Full Name</label>
                    <input
                      name="name"
                      type="text"
                      value={applicationForm.name}
                      onChange={handleApplicationChange}
                      placeholder="Enter your name"
                      required
                    />
                  </div>

                  <div className="career-input-group">
                    <label>Email</label>
                    <input
                      name="email"
                      type="email"
                      value={applicationForm.email}
                      onChange={handleApplicationChange}
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  <div className="career-input-group">
                    <label>Phone</label>
                    <input
                      name="phone"
                      type="tel"
                      value={applicationForm.phone}
                      onChange={handleApplicationChange}
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>
                </div>

                <div className="career-input-group">
                  <label>
                    <FaFilePdf /> Resume PDF
                  </label>
                  <input
                    key={fileInputKey}
                    name="resumeFile"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handleResumeChange}
                    required
                  />
                  {resumeFile && (
                    <small className="career-file-note">
                      Selected: {resumeFile.name}
                    </small>
                  )}
                </div>

                <div className="career-input-group">
                  <label>Message</label>
                  <textarea
                    name="message"
                    rows="4"
                    value={applicationForm.message}
                    onChange={handleApplicationChange}
                    placeholder="Add your qualification, experience, or availability"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="career-submit-btn"
                  disabled={isSubmittingApplication}
                >
                  {renderSubmitButtonText()}
                </button>

                {applicationStatus && (
                  <p className="career-admin-status">
                    <FaCheckCircle /> {applicationStatus}
                  </p>
                )}
              </form>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default CareerDetail;
